/*
 * rheincool — Rechner-Basisklasse (Umsetzungsplan 1.3, HANDOFF.md §8.3)
 * Gemeinsame Mechanik aller Ratgeber-Rechner:
 * - Konstanten aus #rc-constants (zentrale Preise — NIE eigene Werte)
 * - Konfiguration aus dem Section-eigenen <script type="application/json">
 * - Slider ↔ Zahlenfeld-Synchronisation (Tastatur-Alternative, A11y §11)
 * - Start-/Ergebnis-Zustand (gestrichelte Box → Ergebnis + CTA)
 * - aria-live="polite"-Announce
 * - Mobiler Sticky-CTA nur solange das Modul im Viewport ist
 * - Tracking + Store-Publikation
 *
 * Subklassen implementieren compute() und renderResult(result).
 */
class RcCalculator extends HTMLElement {
  connectedCallback() {
    this.constants = this.readJson('rc-constants', document) || {};
    const configEl = this.querySelector('script[type="application/json"]');
    this.config = configEl ? this.parse(configEl.textContent) : {};
    this.resultBox = this.querySelector('[data-calc-result]');
    this.liveRegion = this.querySelector('[data-calc-live]');
    this.stickyCta = this.querySelector('[data-calc-sticky]');
    this.started = false;

    this.syncSliders();
    this.bindInputs();
    this.initSticky();
    this.init();
  }

  /* Hook für Subklassen */
  init() {}

  readJson(id, root) {
    const el = (root || this).querySelector(`#${id}`) || document.getElementById(id);
    return el ? this.parse(el.textContent) : null;
  }

  parse(text) {
    try {
      return JSON.parse(text);
    } catch {
      return {};
    }
  }

  /* --- Slider immer mit Zahlenfeld (A11y) --- */
  syncSliders() {
    this.querySelectorAll('[data-calc-slider]').forEach((slider) => {
      const field = this.querySelector(`[data-calc-field="${slider.dataset.calcSlider}"]`);
      if (!field) return;
      slider.addEventListener('input', () => {
        field.value = slider.value;
        this.update();
      });
      field.addEventListener('input', () => {
        const value = Math.min(
          parseFloat(slider.max),
          Math.max(parseFloat(slider.min), parseFloat(field.value) || parseFloat(slider.min))
        );
        slider.value = value;
        this.update();
      });
    });
  }

  bindInputs() {
    this.addEventListener('change', (event) => {
      if (event.target.matches('input, select')) this.update();
    });
    this.addEventListener('click', (event) => {
      const segment = event.target.closest('[data-calc-segment]');
      if (!segment) return;
      const group = segment.closest('[data-calc-segments]');
      group.querySelectorAll('[data-calc-segment]').forEach((s) => {
        s.classList.toggle('is-active', s === segment);
        s.setAttribute('aria-checked', s === segment ? 'true' : 'false');
      });
      this.update();
    });
    // Segment-Gruppen: Pfeiltasten (Radiogroup-Pattern)
    this.querySelectorAll('[data-calc-segments]').forEach((group) => {
      group.addEventListener('keydown', (event) => {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
        if (!keys.includes(event.key)) return;
        event.preventDefault();
        const segments = Array.from(group.querySelectorAll('[data-calc-segment]'));
        const current = segments.indexOf(document.activeElement);
        const dir = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
        const next = segments[(current + dir + segments.length) % segments.length];
        next.focus();
        next.click();
      });
    });
  }

  segmentValue(name) {
    const active = this.querySelector(`[data-calc-segments="${name}"] .is-active`);
    return active ? active.dataset.value : null;
  }

  fieldValue(name) {
    const field = this.querySelector(`[data-calc-field="${name}"]`);
    return field ? parseFloat(field.value) : NaN;
  }

  update() {
    if (!this.started) {
      this.started = true;
      this.track(`${this.trackPrefix || 'calculator'}_used`, this.trackParams ? this.trackParams() : {});
    }
    const result = this.compute();
    if (!result) return;
    if (this.resultBox) this.resultBox.classList.add('has-result');
    this.renderResult(result);
    if (this.storeKey) window.rcStore?.set(this.storeKey, result);
  }

  announce(text) {
    if (this.liveRegion) this.liveRegion.textContent = text;
  }

  /* Deutsche Formatierung */
  formatNumber(value, decimals = 0) {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  }

  formatEuro(value, decimals = 0) {
    return `${this.formatNumber(value, decimals)} €`;
  }

  /* Mobiler Sticky-CTA nur solange Modul sichtbar (HANDOFF §8.3.8) */
  initSticky() {
    if (!this.stickyCta || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const showable = entry.isIntersecting && this.resultBox?.classList.contains('has-result');
        this.stickyCta.hidden = !showable;
      },
      { threshold: 0.05 }
    );
    observer.observe(this);
  }

  track(event, params = {}) {
    if (window.rcTrack) window.rcTrack(event, params);
  }
}

window.RcCalculator = RcCalculator;
